import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Mic, Square, Pause, Play, Trash2, Send, Globe, MessageSquareShare, Languages, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import SpeakButton from "@/components/SpeakButton";

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "te", label: "తెలుగు" },
];

export default function VoiceNotesPanel({ clinicId }) {
  const [notes, setNotes] = useState([]);
  const [recDialog, setRecDialog] = useState(false);
  const [translations, setTranslations] = useState({});  // {noteId: {lang: text}}
  const [activeLang, setActiveLang] = useState({});  // {noteId: lang}
  const [loadingTrans, setLoadingTrans] = useState({});

  const load = () => api.get(`/clinics/${clinicId}/voice-notes`).then((r) => setNotes(r.data));
  useEffect(() => { load(); }, [clinicId]); // eslint-disable-line

  const playAudio = (noteId) => {
    const token = localStorage.getItem("pulse_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinics/${clinicId}/voice-notes/${noteId}/audio`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(b => {
      const a = new Audio(URL.createObjectURL(b));
      a.play();
    });
  };

  const translate = async (note, lang) => {
    if ((translations[note.id] || {})[lang]) {
      setActiveLang({ ...activeLang, [note.id]: lang });
      return;
    }
    setLoadingTrans({ ...loadingTrans, [note.id]: lang });
    try {
      const r = await api.post(`/clinics/${clinicId}/voice-notes/${note.id}/translate`, { target_lang: lang });
      setTranslations({ ...translations, [note.id]: { ...(translations[note.id] || {}), [lang]: r.data.text } });
      setActiveLang({ ...activeLang, [note.id]: lang });
    } catch { toast.error("Translation failed"); } finally {
      setLoadingTrans({ ...loadingTrans, [note.id]: null });
    }
  };

  const sendToPatient = async (note) => {
    const phone = note.patient_phone;
    if (!phone) return toast.error("Patient phone missing");
    try {
      const r = await api.post(`/clinics/${clinicId}/voice-notes/${note.id}/send-whatsapp`, { patient_phone: phone });
      toast.success(`Voice note link sent to ${phone}${r.data.whatsapp?.simulated ? " (simulated)" : ""}`);
      navigator.clipboard?.writeText(r.data.audio_url);
      setTimeout(() => toast.message("Audio link copied", { description: r.data.audio_url }), 300);
    } catch { toast.error("Send failed"); }
  };

  return (
    <div className="solid-panel p-0 overflow-hidden shadow-3d">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold flex items-center gap-2"><Mic className="h-4 w-4 text-[#f37021]" /> Voice Notes</h3>
          <p className="text-sm text-slate-500">Patient voice notes from WhatsApp appear here automatically. Record one for a patient too.</p>
        </div>
        <Button onClick={() => setRecDialog(true)} className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="open-record-dialog">
          <Mic className="h-4 w-4 mr-2" /> Record for patient
        </Button>
      </div>

      {notes.length === 0 && (
        <div className="p-12 text-center text-slate-400 text-sm">
          <Mic className="h-10 w-10 mx-auto opacity-40 mb-2" />
          No voice notes yet. Patients can send a WhatsApp voice note — it'll auto-transcribe and appear here.
        </div>
      )}

      <div className="divide-y divide-slate-100">
        {notes.map((n) => {
          const lang = activeLang[n.id] || n.detected_language || "en";
          const displayText = (translations[n.id] || {})[lang] || (lang === n.detected_language ? n.transcript : null);
          return (
            <div key={n.id} className="px-6 py-4" data-testid={`voice-note-${n.id}`}>
              <div className="flex items-start gap-4">
                <div className={`h-12 w-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${n.direction === "inbound" ? "bg-[#e6f0ff] text-[#004a99]" : "bg-[#ffefe6] text-[#f37021]"}`}>
                  <Volume2 className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">{n.patient_name}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${n.direction === "inbound" ? "bg-[#e6f0ff] text-[#004a99]" : "bg-[#ffefe6] text-[#f37021]"}`}>
                      {n.direction === "inbound" ? "PATIENT → DOCTOR" : "DOCTOR → PATIENT"}
                    </span>
                    <span className="text-xs text-slate-500">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
                    <Languages className="h-3 w-3" /> Detected: {LANGS.find(l => l.code === n.detected_language)?.label || n.detected_language}
                  </div>

                  <div className="mt-3 text-slate-800 text-sm leading-relaxed flex items-start gap-2">
                    {loadingTrans[n.id] ? (
                      <span className="text-slate-400 italic">Translating…</span>
                    ) : displayText ? (
                      <>
                        <span className="flex-1">{displayText}</span>
                        <SpeakButton text={displayText} lang={lang} testId={`speak-vn-${n.id}`} />
                      </>
                    ) : (
                      <span className="text-slate-400 italic">No transcript</span>
                    )}
                  </div>

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => playAudio(n.id)} className="border-[#004a99] text-[#004a99]" data-testid={`play-${n.id}`}>
                      <Play className="h-3.5 w-3.5 mr-1" /> Listen
                    </Button>
                    <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid={`lang-switcher-${n.id}`}>
                      {LANGS.map((l) => (
                        <button key={l.code} onClick={() => translate(n, l.code)}
                                className={`px-3 py-1 text-xs font-semibold transition ${lang === l.code ? "bg-[#004a99] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                                data-testid={`lang-${n.id}-${l.code}`}>
                          {l.label}
                        </button>
                      ))}
                    </div>
                    {n.direction === "outbound" && n.patient_phone && (
                      <Button size="sm" onClick={() => sendToPatient(n)} className="bg-[#25D366] hover:bg-[#1ea655] text-white" data-testid={`send-vn-${n.id}`}>
                        <MessageSquareShare className="h-3.5 w-3.5 mr-1" /> Send on WhatsApp
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <RecordDialog open={recDialog} onOpenChange={setRecDialog} clinicId={clinicId} onSaved={load} />
    </div>
  );
}

function RecordDialog({ open, onOpenChange, clinicId, onSaved }) {
  const [patientName, setPatientName] = useState("Priya Verma");
  const [patientPhone, setPatientPhone] = useState("+919811000010");
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [seconds, setSeconds] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!open) {
      stop();
      setAudioBlob(null);
      setAudioUrl(null);
      setSeconds(0);
    }
    // eslint-disable-next-line
  }, [open]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      setRecording(true);
      setSeconds(0);
      tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecording(false);
  };

  const upload = async () => {
    if (!audioBlob) return toast.error("Record something first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", audioBlob, "doctor-note.webm");
      fd.append("patient_name", patientName);
      if (patientPhone) fd.append("patient_phone", patientPhone);
      const token = localStorage.getItem("pulse_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinics/${clinicId}/voice-notes/from-doctor`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) throw new Error("upload");
      toast.success("Voice note saved and transcribed");
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Record voice note for patient</DialogTitle>
          <p className="text-sm text-slate-500">Whisper auto-transcribes. Send the audio link via WhatsApp afterwards.</p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Patient name</Label>
            <Input value={patientName} onChange={(e) => setPatientName(e.target.value)} data-testid="rec-patient-name" />
          </div>
          <div>
            <Label>Patient phone</Label>
            <Input value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} data-testid="rec-patient-phone" />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#e6f0ff] to-white border border-slate-200 p-6">
          <button
            onClick={recording ? stop : start}
            className={`h-24 w-24 rounded-full flex items-center justify-center transition-all shadow-xl ${recording ? "bg-[#ef4444] scale-110 animate-pulse" : "bg-[#f37021]"} text-white`}
            data-testid="rec-toggle-btn"
          >
            {recording ? <Square className="h-9 w-9" /> : <Mic className="h-9 w-9" />}
          </button>
          <div className="ml-5">
            <div className="tiny-label">{recording ? "Recording…" : audioBlob ? "Ready" : "Tap to record"}</div>
            <div className="font-heading text-3xl text-slate-900 mt-1">
              {Math.floor(seconds / 60).toString().padStart(2, "0")}:{(seconds % 60).toString().padStart(2, "0")}
            </div>
          </div>
        </div>

        {audioUrl && (
          <div className="mt-3">
            <audio src={audioUrl} controls className="w-full" data-testid="rec-preview-audio" />
            <button onClick={() => { setAudioBlob(null); setAudioUrl(null); setSeconds(0); }} className="mt-2 text-xs text-slate-500 hover:text-rose-600 flex items-center gap-1">
              <Trash2 className="h-3 w-3" /> Discard
            </button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={upload} disabled={busy || !audioBlob} className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="rec-save-btn">
            <Send className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Save & transcribe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
